import React, { ChangeEvent, useMemo, useState } from 'react';
import { GraphData } from '../types';
import {
  COLLECTION_TYPE_FIELD,
  GraphImportDraft,
  ImportCollection,
  SchemaMapping,
  normalizeGraphFromImportDraft,
  parseGraphImportFile
} from '../utils/graphImport';

interface GraphImportPanelProps {
  onClose: () => void;
  onImport: (graphData: GraphData, sourceName: string, warnings: string[]) => void;
}

interface FieldSelectProps {
  label: string;
  value: string;
  fields: string[];
  onChange: (value: string) => void;
  allowCollectionType?: boolean;
}

const FieldSelect = ({
  label,
  value,
  fields,
  onChange,
  allowCollectionType = false
}: FieldSelectProps) => (
  <label className="block">
    <span className="block text-[10px] font-mono uppercase text-vercel-gray mb-1">{label}</span>
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="w-full h-8 rounded border border-vercel-border bg-white px-2 text-xs font-mono text-vercel-black focus:outline-none focus:border-vercel-black"
    >
      {allowCollectionType && (
        <option value={COLLECTION_TYPE_FIELD}>Use collection name</option>
      )}
      {fields.map(field => (
        <option key={field} value={field}>{field}</option>
      ))}
    </select>
  </label>
);

const CollectionSelect = ({
  label,
  value,
  collections,
  onChange
}: {
  label: string;
  value: string;
  collections: ImportCollection[];
  onChange: (value: string) => void;
}) => (
  <label className="block">
    <span className="block text-[10px] font-mono uppercase text-vercel-gray mb-1">{label}</span>
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="w-full h-8 rounded border border-vercel-border bg-white px-2 text-xs font-mono text-vercel-black focus:outline-none focus:border-vercel-black"
    >
      {collections.map(collection => (
        <option key={collection.key} value={collection.key}>
          {collection.label} ({collection.rows.length.toLocaleString()})
        </option>
      ))}
    </select>
  </label>
);

const firstValidField = (fields: string[], current: string, fallback: string) => {
  if (current === COLLECTION_TYPE_FIELD) return current;
  return fields.includes(current) ? current : fields[0] || fallback;
};

const sampleText = (collection: ImportCollection | null): string => {
  if (!collection || collection.rows.length === 0) return 'No rows';
  const sample = collection.rows[0];
  const entries = Object.entries(sample).slice(0, 8);
  return entries.map(([key, value]) => `${key}: ${String(value).slice(0, 40)}`).join(' | ');
};

const GraphImportPanel = ({ onClose, onImport }: GraphImportPanelProps) => {
  const [draft, setDraft] = useState<GraphImportDraft | null>(null);
  const [mapping, setMapping] = useState<SchemaMapping | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const selectedNodeCollection = useMemo(() => (
    draft?.nodeCollections.find(collection => collection.key === mapping?.nodeCollectionKey) || null
  ), [draft, mapping?.nodeCollectionKey]);

  const selectedEdgeCollection = useMemo(() => (
    draft?.edgeCollections.find(collection => collection.key === mapping?.edgeCollectionKey) || null
  ), [draft, mapping?.edgeCollectionKey]);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setError('');
    try {
      const nextDraft = await parseGraphImportFile(file);
      setDraft(nextDraft);
      setMapping(nextDraft.mapping);
    } catch (err) {
      setDraft(null);
      setMapping(null);
      setError(err instanceof Error ? err.message : 'Could not parse graph file.');
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  const updateMapping = (patch: Partial<SchemaMapping>) => {
    setMapping(prev => prev ? { ...prev, ...patch } : prev);
  };

  const handleNodeCollectionChange = (key: string) => {
    if (!draft || !mapping) return;
    const collection = draft.nodeCollections.find(item => item.key === key);
    updateMapping({
      nodeCollectionKey: key,
      nodeIdField: firstValidField(collection?.fields || [], mapping.nodeIdField, ''),
      nodeTypeField: firstValidField(collection?.fields || [], mapping.nodeTypeField, COLLECTION_TYPE_FIELD)
    });
  };

  const handleEdgeCollectionChange = (key: string) => {
    if (!draft || !mapping) return;
    const collection = draft.edgeCollections.find(item => item.key === key);
    updateMapping({
      edgeCollectionKey: key,
      edgeSourceField: firstValidField(collection?.fields || [], mapping.edgeSourceField, ''),
      edgeTargetField: firstValidField(collection?.fields || [], mapping.edgeTargetField, ''),
      edgeTypeField: firstValidField(collection?.fields || [], mapping.edgeTypeField, COLLECTION_TYPE_FIELD)
    });
  };

  const handleImport = () => {
    if (!draft || !mapping) return;
    setError('');
    try {
      const result = normalizeGraphFromImportDraft(draft, mapping);
      onImport(result.graphData, draft.sourceName, result.warnings);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not import graph with this schema.');
    }
  };

  const canImport = !!draft && !!mapping && !!selectedNodeCollection && !!selectedEdgeCollection;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[88vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Import Graph</h2>
            <div className="text-xs font-mono text-vercel-gray mt-1">JSON, NDJSON, Neo4j Browser graph JSON, or APOC export JSON</div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            title="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <label className="block rounded border border-dashed border-vercel-border bg-vercel-bg p-4 cursor-pointer hover:border-vercel-black transition-colors">
            <input
              type="file"
              accept=".json,.ndjson,application/json"
              className="sr-only"
              onChange={handleFileChange}
            />
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-sm font-mono font-semibold text-vercel-black">Upload graph export</div>
                <div className="text-xs font-mono text-vercel-gray mt-1">Choose a local file; the data stays in this browser session.</div>
              </div>
              <div className="px-3 py-1.5 text-xs font-mono rounded border border-vercel-black bg-vercel-black text-white">
                {loading ? 'Reading...' : 'Choose File'}
              </div>
            </div>
          </label>

          {error && (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs font-mono text-red-700">
              {error}
            </div>
          )}

          {draft && mapping && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded border border-vercel-border p-3">
                  <div className="text-[10px] font-mono uppercase text-vercel-gray">Format</div>
                  <div className="text-sm font-mono font-semibold text-vercel-black mt-1">{draft.formatLabel}</div>
                </div>
                <div className="rounded border border-vercel-border p-3">
                  <div className="text-[10px] font-mono uppercase text-vercel-gray">Nodes</div>
                  <div className="text-sm font-mono font-semibold text-vercel-black mt-1">
                    {(selectedNodeCollection?.rows.length || 0).toLocaleString()}
                  </div>
                </div>
                <div className="rounded border border-vercel-border p-3">
                  <div className="text-[10px] font-mono uppercase text-vercel-gray">Edges</div>
                  <div className="text-sm font-mono font-semibold text-vercel-black mt-1">
                    {(selectedEdgeCollection?.rows.length || 0).toLocaleString()}
                  </div>
                </div>
              </div>

              {draft.warnings.length > 0 && (
                <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-mono text-amber-800">
                  {draft.warnings.join(' ')}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded border border-vercel-border p-3 space-y-3">
                  <div className="text-xs font-mono font-semibold text-vercel-black">Nodes</div>
                  <CollectionSelect
                    label="Collection"
                    value={mapping.nodeCollectionKey}
                    collections={draft.nodeCollections}
                    onChange={handleNodeCollectionChange}
                  />
                  <FieldSelect
                    label="ID"
                    value={mapping.nodeIdField}
                    fields={selectedNodeCollection?.fields || []}
                    onChange={(value) => updateMapping({ nodeIdField: value })}
                  />
                  <FieldSelect
                    label="Type"
                    value={mapping.nodeTypeField}
                    fields={selectedNodeCollection?.fields || []}
                    onChange={(value) => updateMapping({ nodeTypeField: value })}
                    allowCollectionType
                  />
                  <div className="text-[10px] font-mono text-vercel-gray leading-relaxed">
                    {sampleText(selectedNodeCollection)}
                  </div>
                </div>

                <div className="rounded border border-vercel-border p-3 space-y-3">
                  <div className="text-xs font-mono font-semibold text-vercel-black">Edges</div>
                  <CollectionSelect
                    label="Collection"
                    value={mapping.edgeCollectionKey}
                    collections={draft.edgeCollections}
                    onChange={handleEdgeCollectionChange}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <FieldSelect
                      label="Source"
                      value={mapping.edgeSourceField}
                      fields={selectedEdgeCollection?.fields || []}
                      onChange={(value) => updateMapping({ edgeSourceField: value })}
                    />
                    <FieldSelect
                      label="Target"
                      value={mapping.edgeTargetField}
                      fields={selectedEdgeCollection?.fields || []}
                      onChange={(value) => updateMapping({ edgeTargetField: value })}
                    />
                  </div>
                  <FieldSelect
                    label="Type"
                    value={mapping.edgeTypeField}
                    fields={selectedEdgeCollection?.fields || []}
                    onChange={(value) => updateMapping({ edgeTypeField: value })}
                    allowCollectionType
                  />
                  <div className="text-[10px] font-mono text-vercel-gray leading-relaxed">
                    {sampleText(selectedEdgeCollection)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-gray-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={!canImport}
            className="px-3 py-1.5 text-xs font-medium text-white bg-black rounded hover:bg-vercel-gray disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            Import
          </button>
        </div>
      </div>
    </div>
  );
};

export default GraphImportPanel;
